/**
 * The Complete step's standard look on a custom site (telify-pages.html, the Complete step): the
 * booking's offering, which form this is, its description and Finish by, then the fields through
 * the app's FormRenderer, the privacy agreement on a first-time client's registration, and the
 * button. A step that could not be read says so in the app's words with Try again and Back to
 * booking. Draws the behavior half; holds no rule.
 */
import { FormRenderer } from '@/components/forms/FormRenderer';
import { PrivacyConsentRowView } from '@/components/forms/PrivacyConsentRowView';
import { STEP_FAILURE } from '@/pages/CompleteBookingPage/completeStepParts';
import { ctaLabel } from '@/pages/DetailsPage/formHeader';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { CompleteStepRuntime } from './useCompleteStepPiece';
import css from './CompleteStep.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** Each form sends in one go: FormRenderer's blur and interval save has nowhere to go. */
function noSave() {}

export function CompleteStepView({ state, actions, form, serverErrors }: CompleteStepRuntime) {
  const { status } = state;
  if (status === 'error') {
    return (
      <section className={css.failed} role="alert" {...pieceRoot(contract)} {...part('failed')}>
        <h1 className={css.failedTitle}>{STEP_FAILURE.title}</h1>
        <p className={css.failedText}>{STEP_FAILURE.message}</p>
        <div className={css.failedActions}>
          <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={actions.retry} {...part('retry')}>
            Try again
          </button>
          <button type="button" className={`${shared.btn} ${shared.btnOutline}`} onClick={actions.back} {...part('back')}>
            Back to booking
          </button>
        </div>
      </section>
    );
  }

  if (status === 'loading' || !form) {
    return (
      <div className={css.loading} role="status" {...pieceRoot(contract)} {...part('page')}>
        Loading…
      </div>
    );
  }

  return (
    <div className={css.col} {...pieceRoot(contract)} {...part('page')}>
      <div className={css.head}>
        <h1 className={css.title} {...part('title')}>
          {state.title}
        </h1>
        <p className={css.line} {...part('step')}>
          {state.step}
        </p>
        {state.description && (
          <p className={css.desc} {...part('description')}>
            {state.description}
          </p>
        )}
        {state.finishBy && (
          <div className={css.finishBy} {...part('finishBy')}>
            <Icon name="clock" small />
            Finish by {state.finishBy}
          </div>
        )}
      </div>
      <div className={css.fields}>
        <FormRenderer
          key={form.formId}
          form={form}
          fieldsAttributes={part('fields')}
          onSubmit={actions.submit}
          onSave={noSave}
          isSubmitting={status === 'submitting'}
          serverErrors={serverErrors}
          disableAutoSave
          appearance="clientBooking"
          renderActions={({ formReady, isSubmitting }) => (
            <>
              {state.consentRequired && (
                <PrivacyConsentRowView
                  checked={state.consented}
                  onChange={actions.setConsent}
                  showError={state.consentError}
                  errorId="web-complete-consent-error"
                  parts={{ row: part('consent'), link: part('policyLink'), error: part('consentError') }}
                />
              )}
              <div className={css.bar}>
                <button
                  type="submit"
                  className={`${shared.btn} ${shared.btnPrimary} ${css.continue}`}
                  disabled={isSubmitting}
                  {...part('continue', isSubmitting && 'disabled')}
                >
                  {ctaLabel({ formType: form.formType, isLastForm: state.lastForm, hasPaymentAhead: state.paymentAhead, formReady })}
                  <Icon name="arrow" />
                </button>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}
